import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Subject } from 'rxjs';
import { RestErrorType, RestErrorEventCategory } from '../../domain/error.domain';
import  { SessionService } from '../session.service';
import  { RestJsonCodecService } from './rest-json-codec.service';
import type { HttpErrorResponse } from '@angular/common/http';

import type { BackendError, BackendErrorResponse } from '../../domain/error.domain';

export interface RestErrorHandler {
  handleErrors(errors: Array<BackendError>): BackendErrorResponse;
}

export const REST_ERROR_HANDLERS = new InjectionToken<Array<RestErrorHandler>>('restErrorHandlers');

@Injectable({
  providedIn: 'root'
})
export class RestErrorHandlerService {

  readonly onError: Subject<BackendError>;

  constructor(@Inject(SessionService) private sessionService: SessionService,
              @Inject(RestJsonCodecService) private restJsonCodecService: RestJsonCodecService,
              @Inject(REST_ERROR_HANDLERS)
              private restErrorHandlers: Array<RestErrorHandler>) {
    this.onError = new Subject<BackendError>();
  }

  addHandler(restErrorHandler: RestErrorHandler): void {
    this.restErrorHandlers.push(restErrorHandler);
  }

  error(errorResponse: HttpErrorResponse): void {
    // status could be zero or negative in some scenarios - for example when request has been cancelled
    // or not loaded because of cross origin issues
    // TODO: roustam: Ignoring 429 (too many requests). We are doing this temporarily should handle it better
    if (errorResponse.status > 0 && errorResponse.status !== 429) {
      const errors = this.parseErrors(errorResponse);
      if (this.hasErrors(errors)) {
        this.handleErrors(errors);
      } else if (!this.sessionService.hasSessionTimedOut(errorResponse.headers, errorResponse.error)) {
        // we've got and error without a body - not much we can do
        this.onError.next({ type: RestErrorType.SYSTEM, code: 'UNKNOWN' });
      }
      // session timeouts will be ignored
    }
  }

  private handleErrors(errors: Array<BackendError>): void {
    if (this.restErrorHandlers !== undefined) {
      for (const handler of this.restErrorHandlers) {
        const errorFound = this.handlerError(handler, errors);
        if (errorFound === true) {
          break;
        }
      }
    }
  }

  private handlerError(handler: RestErrorHandler, errors: Array<BackendError>): boolean {
    const result: BackendErrorResponse = handler.handleErrors(errors);
    if (result !== undefined && result.category === RestErrorEventCategory.ERROR) {
      this.onError.next(result);
      return true;
    }
    return false;
  }

  private parseErrors(errorResponse: HttpErrorResponse): Array<BackendError> {
    let error: { errors: Array<BackendError>};

    if (errorResponse !== undefined && typeof errorResponse.error === 'string') {
      error = this.restJsonCodecService.decode(errorResponse.error) ;
    }
    return error === undefined || !Array.isArray(error.errors) ? [] : error.errors;
  }

  private hasErrors(errors: Array<BackendError>): boolean {
    return Array.isArray(errors) && errors.length > 0;
  }

}
